import React, { useContext, useEffect, useState } from "react";

import Tippy from "@tippyjs/react";
import cx from "classnames";
import EventEmitter from "events";
import { graphql } from "relay-runtime";

import { Views } from "~/actionItems/constants";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import { getIntegrationForKey } from "~/integrations/actionItemIntegration/index";
import { ReactComponent as AsanaLogo } from "~/legacy-assets/images/logos/asana.svg";
import type {
	ActionItemIntegrationLinkNode,
	ActionItemIntegrationLinks,
} from "~/lib/prose/plugins/actionItemIntegrationLink";
import { clickAsanaLink } from "~/lib/prose/plugins/asanaItems";
import { inCalendarMode, inVideocallMode, isMobile, setTooltip } from "~/lib/utils";

import type { WidgetActionItemIntegrationLinkDummyWidgetFragment$data } from "~/__generated__/WidgetActionItemIntegrationLinkDummyWidgetFragment.graphql";
import type { WidgetAsanaDummyWidgetFragment$data } from "~/__generated__/WidgetAsanaDummyWidgetFragment.graphql";

import styles from "~/integrations/actionItemIntegration/css/pill.module.css";

export function ActionItemIntegrationLinkWidget({
	actionItemLink: initialActionItemLink,
	integrationLinkSpecificEmitter,
	customClassName,
}: {
	actionItemLink: ActionItemIntegrationLinks[string];
	integrationLinkSpecificEmitter?: EventEmitter;
	customClassName?: string;
}) {
	const [actionItemLink, setActionItemLink] = useState<ActionItemIntegrationLinkNode>(initialActionItemLink);

	useEffect(() => {
		const listener = (data: null | ActionItemIntegrationLinks[string]) => {
			if (!data) return;
			setActionItemLink(data);
		};
		integrationLinkSpecificEmitter?.addListener("data", listener);
		return () => {
			integrationLinkSpecificEmitter?.removeListener("data", listener);
		};
	}, [setActionItemLink, integrationLinkSpecificEmitter]);

	const integration = getIntegrationForKey(actionItemLink.integrationProvider);
	const RenderPill = integration.renderPill;

	if (!RenderPill) {
		throw new Error(`Can't render pill for this integration ${integration.displayName}`);
	}

	const errored = actionItemLink.syncStatus === "FAILED_TO_SYNC_EXTERNALLY";

	if (errored) {
		return (
			<Tippy content={`Failed to Sync to ${integration.displayName}`} className={styles.toolTip}>
				<WidgetInner logo={integration.logo} updating={false} customClassName={customClassName}>
					Sync Failed
				</WidgetInner>
			</Tippy>
		);
	}

	const updating = actionItemLink.syncStatus !== "DONE_SYNCING_EXTERNALLY";

	return (
		<Tippy content={`Syncing to ${integration.displayName}`} className={styles.toolTip} disabled={!updating}>
			<RenderPill actionItemLink={actionItemLink} customClassName={customClassName} />
		</Tippy>
	);
}

export function WidgetInner({
	tooltip,
	url,
	updating,
	logo,
	children,
	customClassName,
}: React.PropsWithChildren<{
	tooltip?: string;
	url?: string;
	logo: React.ReactNode;
	updating: boolean;
	customClassName?: string;
}>) {
	const args = !updating && tooltip ? { ...setTooltip(tooltip, "bottom center") } : {};

	const { view } = useContext(ActionItemsContext);
	const smallPill = (isMobile || inVideocallMode || inCalendarMode) && view === Views.STREAM_TAB;

	return (
		<a {...args} className={cx(styles.pill, customClassName)} href={url} target="_blank" rel="noopener noreferrer">
			<div className={cx(styles.greyscaleBase, { [styles.greyscale]: updating })}>{logo}</div>
			{!smallPill && <div className={styles.projectName}>{children}</div>}
		</a>
	);
}

graphql`
	fragment WidgetActionItemIntegrationLinkDummyWidgetFragment on NoteBlock {
		actionItemIntegrationLink {
			integrationProvider
			integrationData {
				...clickupSpecificData @relay(mask: false)
				...trelloSpecificData @relay(mask: false)
				...mondaySpecificData @relay(mask: false)
				...linearSpecificData @relay(mask: false)
				...microsoftTodoSpecificData @relay(mask: false)
				...hubspotTaskSpecificData @relay(mask: false)
			}
			syncStatus
		}
	}
`;

type ActionItemIntegrationDummyWidgetProps = {
	actionItemIntegrationLink: WidgetActionItemIntegrationLinkDummyWidgetFragment$data["actionItemIntegrationLink"];
};

export function ActionItemIntegrationLinkDummyWidget({
	actionItemIntegrationLink: integrationLink,
	customClassName,
}: ActionItemIntegrationDummyWidgetProps & {
	customClassName?: string;
}) {
	if (!integrationLink) return <></>;

	const integration = getIntegrationForKey(integrationLink.integrationProvider);
	const RenderPill = integration.renderPill;

	if (!RenderPill) {
		throw new Error(`Can't render pill for this integration ${integration.displayName}`);
	}

	return <RenderPill actionItemLink={integrationLink} customClassName={customClassName} />;
}

type AsanaDummyWidgetProps = {
	asanablocklink: WidgetAsanaDummyWidgetFragment$data["asanablocklink"];
};

graphql`
	fragment WidgetAsanaDummyWidgetFragment on NoteBlock {
		asanablocklink {
			project {
				name
				externalId
			}
			externalId
		}
	}
`;

export function AsanaDummyWidget({ asanablocklink }: AsanaDummyWidgetProps) {
	const onClickWidget = React.useCallback(() => {
		clickAsanaLink(asanablocklink?.project.externalId, asanablocklink?.externalId);
	}, [asanablocklink?.externalId, asanablocklink?.project.externalId]);

	if (!asanablocklink) return <></>;

	return (
		<button onClick={onClickWidget} className={styles.pill}>
			<AsanaLogo />
			<div className={styles.projectName}>{asanablocklink.project.name}</div>
		</button>
	);
}
